import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { APP } from '../config/app';
import { useApp } from '../context/AppContext';
import { useLanguage } from '../i18n/LanguageContext';
import {
  cancelIntertownRequest, ensureIntertownShare, listenToIntertownOtp, trackUrlForToken, triggerIntertownSos,
} from '../services/firebase';
import { registerAndroidBackHandler } from '../utils/androidBack';
import { formatDriverName } from '../utils/driverName';
import { hapticSos } from '../utils/haptics';
import { planSosShare, sosToast } from '../utils/intertownSos';
import {
  annaTelUrl, canCancelTrip, LIVE_STATUSES, tripShareText, tripViewState,
} from '../utils/intertownTrip';
import { isNativeApp } from '../utils/platform';
import { intertownErrorKey } from '../utils/rideErrors';
import {
  buildSmsUrl, buildSosTelUrl, buildTelUrl, openWhatsappChat, shareTripText,
} from '../utils/support';
import DriverProfileModal from './DriverProfileModal';
import IntertownLiveMap from './IntertownLiveMap';
import IntertownTripView from './IntertownTripView';
import SupportModal from './SupportModal';

// "Share details" normally has the family link ready (it is made as soon as an Anna is assigned);
// if the server is slow it waits this long for it and then sends the plain details without a link.
const SHARE_LINK_WAIT_MS = 3000;

/**
 * The rider's Intertown trip while it is live: from "finding an Anna" to the end of a return
 * trip. It owns everything the picture needs and nothing about how it looks: Anna's location
 * (from the map), a clock, the arrival time, the waiting charge so far, the trip PIN, and what
 * each button does. IntertownTripView draws it.
 */
export default function IntertownTripScreen({ trip }) {
  const { t, lang } = useLanguage();
  const { userProfile } = useApp();
  const navigate = useNavigate();
  const [nowMs, setNowMs] = useState(() => Date.now());
  const [driverLocation, setDriverLocation] = useState(null);
  const [panned, setPanned] = useState(false);
  const [minimized, setMinimized] = useState(false);
  const [sheetHeight, setSheetHeight] = useState(0);
  const [safetyOpen, setSafetyOpen] = useState(false);
  const [supportOpen, setSupportOpen] = useState(false);
  const [driverOpen, setDriverOpen] = useState(false);
  const [tripOtp, setTripOtp] = useState('');
  const [alerting, setAlerting] = useState(false);
  const [alerted, setAlerted] = useState(false);
  const sheetRef = useRef(null);
  const recenterRef = useRef(null);
  const cancelling = useRef(false);
  const sharing = useRef(false);
  // The family link and the SOS each make one server call that everything after reuses: the share
  // button, the 112 button and the alert button all share the SOS call, so a double tap or a second
  // channel never raises a second alert (the server also keeps one open alert per trip).
  const shareLinkRef = useRef('');
  const shareLinkRequest = useRef(null);
  const sosRequest = useRef(null);

  const status = trip.status;

  // One running clock for "Searching for 02:10", the trip time, the arrival time and the waiting
  // charge. A phone that slept catches up the moment the screen is shown again, instead of
  // waiting for the next tick.
  useEffect(() => {
    const tick = () => setNowMs(Date.now());
    const timer = window.setInterval(tick, 1000);
    const onVisible = () => { if (document.visibilityState === 'visible') tick(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, []);

  // The trip PIN is revealed only once Anna has server-confirmed arrival at the pickup.
  useEffect(() => {
    if (status !== 'arrived_at_pickup') {
      setTripOtp('');
      return undefined;
    }
    return listenToIntertownOtp(trip.id, (value) => setTripOtp(value || ''));
  }, [trip.id, status]);

  // The family's live link is made as soon as an Anna is assigned, so "Share details" can open
  // WhatsApp at once. A failed attempt is retried at the next change of the trip.
  const tripId = trip.id;
  const fetchShareLink = useCallback(() => {
    if (!shareLinkRequest.current) {
      shareLinkRequest.current = ensureIntertownShare(tripId)
        .then((result) => {
          if (result?.trackUrl) shareLinkRef.current = result.trackUrl;
          return shareLinkRef.current;
        })
        .catch(() => {
          shareLinkRequest.current = null;
          return '';
        });
    }
    return shareLinkRequest.current;
  }, [tripId]);
  useEffect(() => {
    if (LIVE_STATUSES.has(status) && !shareLinkRef.current && !trip.shareToken) fetchShareLink();
  }, [status, trip.shareToken, fetchShareLink]);

  // Raises the SOS for MyTownly's safety team (once per open Safety menu) and returns what the phone
  // needs to tell family. A failed call is forgotten so the rider can try again.
  const raiseSos = useCallback(() => {
    if (!sosRequest.current) {
      sosRequest.current = triggerIntertownSos(tripId)
        .then((data) => ({
          trackUrl: String(data?.trackUrl || ''),
          contacts: Array.isArray(data?.contacts) ? data.contacts : [],
        }))
        .catch((error) => {
          sosRequest.current = null;
          throw error;
        });
    }
    return sosRequest.current;
  }, [tripId]);

  // Anna's phone: how fresh it is, the time left that can honestly be said, the waiting so far.
  const view = tripViewState({
    trip, driverLocation, nowMs, captainLocationMaxAgeSec: APP.captainLocationMaxAgeSec,
  });

  // Each new part of the trip opens the sheet again: a rider who tucked it away must still see
  // the PIN the moment Anna arrives.
  useEffect(() => { setMinimized(false); }, [view.phase]);

  // The sheet's height decides how much of the map is really visible.
  useEffect(() => {
    const el = sheetRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const sync = () => setSheetHeight(Math.round(el.getBoundingClientRect().height));
    sync();
    const observer = new ResizeObserver(sync);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const supportTel = buildTelUrl();
  const supportAvailable = supportTel !== '#';

  // The assigned-driver fields live on the request itself (respondToIntertownOfferSecure), so
  // the driver card and profile read them from here.
  const driverProfile = useMemo(() => {
    if (!trip.driverId) return null;
    return {
      id: trip.driverId,
      name: trip.driverName || null,
      phone: trip.driverPhone || null,
      photoUrl: trip.driverPhoto || null,
      vehicleNumber: trip.vehicleNumber || null,
      vehicleModel: trip.vehicleModel || null,
      ratingAvg: trip.driverRatingAvg ?? null,
      totalRides: trip.driverTotalRides ?? null,
      vehicleSafety: trip.vehicleSafety || null,
    };
  }, [
    trip.driverId, trip.driverName, trip.driverPhone, trip.driverPhoto, trip.vehicleNumber,
    trip.vehicleModel, trip.driverRatingAvg, trip.driverTotalRides, trip.vehicleSafety,
  ]);

  const insets = useMemo(() => ({ top: view.delayed ? 150 : 110, bottom: sheetHeight ? sheetHeight + 20 : 320 }), [view.delayed, sheetHeight]);

  // Overlays take Android back first; otherwise the app-level handler (Home) would run and
  // leave the trip with a menu still open.
  const overlayOpen = safetyOpen || supportOpen || driverOpen;
  useEffect(() => {
    if (!overlayOpen) return undefined;
    return registerAndroidBackHandler(() => {
      if (supportOpen) setSupportOpen(false);
      else if (driverOpen) setDriverOpen(false);
      else setSafetyOpen(false);
      return true;
    });
  }, [overlayOpen, supportOpen, driverOpen]);

  const callAnna = () => {
    const url = annaTelUrl(trip.driverPhone);
    if (url) window.location.href = url;
  };

  const shareDetails = async () => {
    if (sharing.current) return;
    sharing.current = true;
    try {
      const driverName = trip.driverName ? formatDriverName(trip.driverName) : '';
      let link = shareLinkRef.current || trackUrlForToken(trip.shareToken);
      if (!link) {
        link = await Promise.race([
          fetchShareLink(),
          new Promise((resolve) => { window.setTimeout(() => resolve(''), SHARE_LINK_WAIT_MS); }),
        ]);
      }
      shareTripText(tripShareText({
        trip, routeText: view.routeText, driverName, link,
      }), 'MyTownly Rides Intertown trip').catch(() => {});
    } finally {
      sharing.current = false;
    }
  };

  // Anyone dialling 112 on a trip should also reach MyTownly: the alert is raised in the background
  // and never delays the call.
  const call112 = () => {
    setSafetyOpen(false);
    hapticSos();
    raiseSos()
      .then(() => toast.success(t('sos.teamNotified')))
      .catch((error) => console.warn('Intertown SOS alert failed', error?.message));
    window.location.href = buildSosTelUrl();
  };

  // The alert button: alerts MyTownly's safety team (confirmed by the server) and opens messages with
  // the live link for the rider's family (the rider still has to press Send).
  const alertTeam = async () => {
    if (alerting) return;
    hapticSos();
    setAlerting(true);
    try {
      const { contacts, trackUrl } = await raiseSos();
      const plan = planSosShare({ contacts, trackUrl, t, city: APP.city });
      plan.forEach((step, i) => {
        window.setTimeout(() => {
          if (step.via === 'whatsapp') {
            openWhatsappChat(step.text, step.phone);
          } else if (buildSmsUrl(step.phone, step.text) !== '#') {
            window.open(buildSmsUrl(step.phone, step.text), '_blank', 'noopener,noreferrer');
          }
        }, i * 400);
      });
      setAlerted(true);
      const note = sosToast(plan);
      toast.success(t(note.key, note.vars));
    } catch (error) {
      console.error(error);
      toast.error(t('sos.alertFailed'));
    } finally {
      setAlerting(false);
    }
  };

  // Each time the menu is opened it starts fresh: the server keeps one open alert per trip and
  // answers a repeat with the same link, so pressing the alert again opens the messages again.
  const openSafety = () => {
    sosRequest.current = null;
    setAlerted(false);
    setSafetyOpen(true);
  };

  const cancelTrip = async () => {
    if (cancelling.current || !canCancelTrip(status) || !window.confirm(t('intertown.confirmCancel'))) return;
    cancelling.current = true;
    try {
      await cancelIntertownRequest(trip.id, 'Cancelled by passenger');
      toast.success(t('intertown.cancelledToast'));
    } catch (error) {
      const key = intertownErrorKey(error);
      toast.error(key ? t(key) : t('intertown.cancelFailed'));
    } finally {
      cancelling.current = false;
    }
  };

  return (
    <>
      <IntertownTripView
        t={t}
        lang={lang}
        trip={trip}
        driverProfile={driverProfile}
        tripOtp={tripOtp}
        {...view}
        nowMs={nowMs}
        minimized={minimized}
        sheetRef={sheetRef}
        sheetHeight={sheetHeight}
        safetyOpen={safetyOpen}
        supportAvailable={supportAvailable}
        alerting={alerting}
        alerted={alerted}
        showBack={!isNativeApp()}
        panned={panned}
        map={(
          <IntertownLiveMap
            key={trip.id}
            trip={trip}
            insets={insets}
            onLocation={setDriverLocation}
            onPanned={setPanned}
            recenterRef={recenterRef}
          />
        )}
        onToggleMinimized={() => setMinimized((value) => !value)}
        onCancel={cancelTrip}
        onCall={callAnna}
        onShowDriver={() => setDriverOpen(true)}
        onShare={shareDetails}
        onOpenSafety={openSafety}
        onCloseSafety={() => setSafetyOpen(false)}
        onCall112={call112}
        onAlert={alertTeam}
        onCallSupport={() => { setSafetyOpen(false); window.location.href = supportTel; }}
        onReport={() => { setSafetyOpen(false); setSupportOpen(true); }}
        onBack={() => navigate('/', { replace: true })}
        onRecenter={() => recenterRef.current?.()}
      />

      {driverOpen && (
        <DriverProfileModal
          driver={driverProfile}
          ride={trip}
          t={t}
          showSafetyReport
          onClose={() => setDriverOpen(false)}
        />
      )}
      {supportOpen && (
        <SupportModal
          open={supportOpen}
          onClose={() => setSupportOpen(false)}
          userProfile={userProfile}
          // What the support sheet reads about this trip (the receipt passes the same shape).
          defaultRide={{
            id: trip.id,
            kind: 'intertown',
            createdAt: trip.createdAt,
            pickup: trip.pickup,
            destination: trip.destination,
            fare: Number(trip.totalFare) || Number(trip.baseFare) || 0,
          }}
          t={t}
        />
      )}
    </>
  );
}
